import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import {
  secondaryText,
  tertiaryText,
  semiBoldText,
  smallTextSize,
  borderPrimary,
  rounded,
} from '../../../styling/stylingHelper';
import { currencyFormatter } from '../../../services/helperService';

/** Native select style of the inventory tabs (the Leakage filters and the "Compared against" picker). */
export const inventorySelectCls = cn('h-11 px-3 border bg-card dark:bg-card-dark', borderPrimary, rounded, smallTextSize, semiBoldText, secondaryText);

export interface ComparedAgainstOption {
  key: string;
  /** The count's formatted date. */
  label: string;
  totalValue: number;
}

/**
 * The "Compared against" slot at the top of the Count Review and Leakage tabs — one component so
 * both tabs show the same thing in the same place. All inventories: a caption (each inventory is
 * compared against its own previous count, plus the ones with nothing to compare yet). One
 * inventory: a picker over its earlier counts, `{date} · {total}`. An empty span when there is
 * nothing to pick, so a row's right-hand controls keep their place.
 */
export function ComparedAgainstSlot({
  isAll,
  skippedNames,
  options,
  value,
  onChange,
  disabled,
}: {
  isAll: boolean;
  skippedNames: string[];
  options: ComparedAgainstOption[];
  value: string;
  onChange: (key: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();

  if (isAll) {
    return (
      <p className={cn(smallTextSize, secondaryText)}>
        {t('inventory.leakage.allGroupsCaption')}
        {skippedNames.length > 0 && (
          <span className={cn(tertiaryText)}> {t('inventory.leakage.skippedGroups', { groups: skippedNames.join(', ') })}</span>
        )}
      </p>
    );
  }
  if (options.length === 0) return <span />;
  return (
    // max-w-full + min-w-0: on a narrow phone the select shrinks (the browser clips its text)
    // instead of pushing the page sideways.
    <label className={cn('flex max-w-full items-center gap-2', smallTextSize, semiBoldText, secondaryText)}>
      {t('inventory.leakage.comparedAgainst')}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={cn(inventorySelectCls, 'min-w-0 disabled:opacity-50')}
      >
        {options.map((o) => (
          <option key={o.key} value={o.key}>
            {o.label} · {currencyFormatter.format(o.totalValue)}
          </option>
        ))}
      </select>
    </label>
  );
}
